import { getRatingsFromSurveyScale } from "@repo/app-types/client/client";
import type {
  ComplexQuestion,
  ComplexQuestionInsert,
} from "@repo/app-types/entities/question";
import { surveyScales } from "@repo/app-types/entities/question";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { InfoIconTooltip } from "@repo/ui/tooltip/infoIconTooltip";
import type { FC } from "react";
import { MAX_CHARACTERS_PER_QUESTION } from "./basicQuestionEditor.js";
import { SurveyOptionsEditor } from "./surveyOptionsEditor.js";

const surveyScalesDropdownOptions = surveyScales.map((key) => ({
  label: key
    .toLowerCase()
    .split("_")
    .join(" / ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase()), // Transform key into human-readable format
  value: key,
}));

interface MatrixQuestionEditorProps {
  question: ComplexQuestion | ComplexQuestionInsert;
  onChange: (question: ComplexQuestion | ComplexQuestionInsert) => void;
}

export const MatrixQuestionEditor: FC<MatrixQuestionEditorProps> = ({
  question,
  onChange,
}) => {
  if (question.type !== "rating") {
    return null;
  }

  return (
    <div>
      <div className="w-full">
        <div>
          <Textarea
            label="Question"
            value={question.question}
            placeholder="Add your survey question..."
            onChange={(newValue) => {
              onChange({
                ...question,
                question: newValue,
              });
            }}
            shouldDisableNewline
            maxLength={MAX_CHARACTERS_PER_QUESTION}
          />
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="w-52 space-y-2">
            <Label size={Size.SMALL} className="flex">
              <span>Rating scale</span>
              <InfoIconTooltip className="mt-0.5 ml-0.5">
                The 5-point scale participants will use to rate your survey
                options. For example:{" "}
                {getRatingsFromSurveyScale(question.scale)
                  .map((r) => r.name)
                  .join(", ")}
                .
              </InfoIconTooltip>
            </Label>
            <SimpleSelect
              label="Rating scale"
              isLabelHidden
              options={surveyScalesDropdownOptions}
              value={question.scale}
              placeholder="Select a scale for this survey"
              onChange={(newScale) => {
                onChange({
                  ...question,
                  scale: newScale,
                });
              }}
            />
          </div>
        </div>
      </div>
      <div className="mt-2">
        <Label size={Size.SMALL}>Options</Label>
      </div>
      <SurveyOptionsEditor
        onOptionsChange={(options) => {
          onChange({
            ...question,
            options,
          });
        }}
        options={question.options}
        maxNumberOfOptions={8}
      />
    </div>
  );
};
